import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import Header from '../components/Header';
import { styled } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { affiliateDataId, affiliateName } from '../slices/affiliateTheme';
import { useNavigate } from "react-router-dom";
import { showConfirm, showError, showSuccess } from "../components/Swal";
import { IThemeGalleryRes, IThemeGalleryReq, ProcessedTheme } from "../types";
import { affiliateId, authSessionid, authUsername, editor, user } from "../slices/auth-slice";
import {
  editorThemeState,
  loadSavedTheme,
} from "../slices/commonSlice/themeSlice";
import { HandleUpdateSaveTheme } from "../commonFunction";
import { PREVIEW_URL, VIPER_URL } from "../commonConstant";
import { themeGallery, themeGalleryLoading } from '../slices/themeGallery-slice';
import Loader from '../Loader';


const PrimaryButton = styled("button")(({ theme }) => ({
  background: "#9f004f",
  color: "#fff",
  border: "1px solid #ccc",
  boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.2)",
  padding: "6px 12px",
  cursor: "pointer",
  transition: "all 0.3s ease",
  fontWeight: "400 !important",
  fontSize: "16px !important",

  '&:hover': {
    background: "#fff",
    color: "#9f004f",
    borderColor: "#ccc",
    boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.5)",
    fontWeight: "400 !important",
  },
}));

export default function ThemeGallery() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const affiliateIdData = useAppSelector(affiliateId);
  const affiliateNameData = useAppSelector(affiliateName);
  const loading = useAppSelector(themeGalleryLoading);
  const userStatus = useAppSelector(user)
  const affiliteId = useAppSelector(affiliateDataId)
  const editorStatus = useAppSelector(editor);

  const [selectedTheme, setSelectedTheme] = useState<ProcessedTheme | null>(null);
  const [themes, setThemes] = useState<ProcessedTheme[]>([]);
  const id: number | null = userStatus ? affiliateIdData ?? null : affiliteId ?? null;
  const userName = useAppSelector(authUsername);
  const sessionId = useAppSelector(authSessionid);

  if (!userStatus && !editorStatus) {
    navigate("/publisher-dashboard")
  }

  useEffect(() => {
    const fetchThemes = async () => {
      try {
        const request: IThemeGalleryReq = {
          username: userName,
          sessionid: sessionId,
          failstatus: 0,
          request: {}
        };

        const response = await dispatch(themeGallery(request)).unwrap() as IThemeGalleryRes;

        if (response && response.status === 0 && response.data && response.data.gallery) {
          const processedThemes: ProcessedTheme[] = response.data.gallery.map(theme => {
            const images = theme.pages.map(page => page.relativepath);

            return {
              id: theme.themeid,
              name: theme.themeid,
              description: theme.description,
              image: images,
              preview: theme.theme
            };
          });

          setThemes(processedThemes);
        } else {
          setThemes([]);
          if (response.status !== 0) {
            showError("Error", response.statusMessage || "Failed to load themes");
          }
        }
      } catch (error: any) {
        showError("Error", error.message || "Failed to load themes");
      }
    };

    fetchThemes();
  }, [dispatch]);

  const handlePreviewClick = (theme: ProcessedTheme) => {
    setSelectedTheme(theme);
  };

  const handleEdit = async () => {
    if (!affiliteId) {
      showError("Error", "Please select Affiliate");
      return;
    }
    try {
      let confirmed = await showConfirm("Confirm", "Are you sure you want to Edit this theme?");
      if (confirmed.isConfirmed) {
        await dispatch(editorThemeState(true));
        await dispatch(
          loadSavedTheme({
            theme: selectedTheme?.preview ?? "",
            editorThemeState: true,
          })
        );
        if (userStatus) {
          navigate("/user-dashboard", { state: { change: true, theme: selectedTheme?.preview ?? "" } });
        } else {
          navigate("/editor-dashboard", { state: { change: true, theme: selectedTheme?.preview ?? "" } });
        };
      }
    } catch (error: any) {
      showError("Error", error.message);
    }
  };

  async function handleApplyTheme() {
    if (!affiliteId) {
      showError("Error", "Please select Affiliate");
      return;
    }
    let confirmed = await showConfirm("Confirm", "Are you sure you want to Save and Raise Publish Request?");
    if (confirmed.isConfirmed) {
      const response = await HandleUpdateSaveTheme(
        userName,
        sessionId,
        "PR",
        userStatus ? affiliateIdData ?? null : affiliteId ?? null,
        selectedTheme?.preview ?? ""
      );

      if (response?.status === 0) {
        showSuccess("Success", "Raise publish request successfully");
      }
      setSelectedTheme(null);
    }
  }

  return (
    <div>
      <Loader loading={loading} />
      <Header affiliateName={affiliateNameData} />
      <div className="container my-4">
        {themes.length !== 0 && (
          <>
            <h3 className="text-center mb-4 pb-3">
              <span className='border-bottom-line text-uppercase' >Theme Gallery</span>
            </h3>
            <div className="row">
              {themes.map((theme) => (
                <div key={theme.id} className="col-md-3 mb-4">
                  <div
                    className="card"
                    onClick={() => handlePreviewClick(theme)}
                    style={{ cursor: 'pointer' }}
                  >
                    <img
                      src={`${VIPER_URL}${theme.image[0]}`}
                      alt={`${theme.name} preview`}
                      className="card-img-top"
                      style={{ height: '350px' }}
                    />
                    <div className="card-body">
                      <h5 className="card-title">{theme.name}</h5>
                      <p className="card-text">
                        {theme.description && theme.description.length > 50
                          ? `${theme.description.substring(0, 50)}...`
                          : theme.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {selectedTheme && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          style={{ background: 'rgba(0, 0, 0, 0.5)' }}
          onClick={() => setSelectedTheme(null)}
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{selectedTheme.name}</h5>
                <button type="button" className="btn-close" onClick={() => setSelectedTheme(null)}></button>
              </div>
              <div className="modal-body px-5 d-flex justify-content-start flex-column">
                <div id="carouselExample" className="carousel slide" data-bs-ride="carousel">
                  <div className="carousel-inner" style={{ height: 550 }}>
                    {selectedTheme.image.map((img, index) => (
                      <div
                        className={`carousel-item ${index === 0 ? 'active' : ''}`}
                        key={index}
                        style={{ height: '100%' }}
                      >
                        <img
                          src={`${VIPER_URL}${img}`}
                          alt={`${selectedTheme.name} preview ${index + 1}`}
                          className="d-block w-100"
                          style={{
                            maxHeight: '100%',
                            objectFit: 'contain',
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  {selectedTheme.image.length > 1 && (
                    <>
                      <button
                        className="carousel-control-prev"
                        type="button"
                        data-bs-target="#carouselExample"
                        data-bs-slide="prev"
                      >
                        <span className="carousel-control-prev-icon" style={{ filter: 'invert(1)' }}></span>
                        <span className="visually-hidden">Previous</span>
                      </button>
                      <button
                        className="carousel-control-next"
                        type="button"
                        data-bs-target="#carouselExample"
                        data-bs-slide="next"
                      >
                        <span className="carousel-control-next-icon" style={{ filter: 'invert(1)' }}></span>
                        <span className="visually-hidden">Next</span>
                      </button>
                    </>
                  )}
                </div>

                <div className="d-flex my-2 gap-2 justify-content-start align-items-start">
                  <h6 className='m-0'>Description:</h6>
                  <p className='m-0'>{selectedTheme.description}</p>
                </div>
                <div className='d-flex gap-2 justify-content-center mt-2'>
                  <PrimaryButton className="btn-success text-uppercase " onClick={handleApplyTheme}>Save and Raise Publish Request</PrimaryButton>
                  <PrimaryButton className="btn-success text-uppercase" onClick={handleEdit}>Edit This Theme in Editor</PrimaryButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}